import DataSyncCgObjectsStore from '../../stores/object/DataSyncCgObjectsStore';
import { DataSyncObjectsStore } from '../../stores/object/DataSyncObjectsStore';
import {
  CGObjectDefinition,
  DataSyncItem,
  Destination,
  MappingMode,
  ObjectDefinition,
  OptimizelyProduct,
  Source
} from '../../types/DataSyncItem';
import { FieldMappingEntry } from '../../types/DataSyncViewModel';
import { FIELD_PATH_SEPARATOR, FieldDefinition, isObjectArray } from '../../types/FieldDefinition';
import { INCOMPLETE } from '../../types/FieldMapping';
import { DataSyncObjectDefinitionService } from './DataSyncObjectDefinitionService';

export class DataSyncMappingService {

  public static async determineMappingMode(
    source: Source,
    destination: Destination,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null
  ): Promise<MappingMode> {
    return await DataSyncMappingService.isCGDestinationInheritedFromSource(
      source, destination, sourceStore, destinationStore
    ) ? MappingMode.FIXED : MappingMode.EDITABLE;
  }

  public static async isCGDestinationInheritedFromSource(
    source: Source,
    destination: Destination,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null
  ): Promise<boolean> {
    const destinationObject = await destinationStore?.getDestinationObjectSchema(
      destination.object.object_name
    );

    const sourceObject = sourceStore?.getSourceObjectDefinition(source.object.object_name);

    if (
      destination.product !== OptimizelyProduct.CGR ||
      !destinationObject ||
      !sourceObject ||
      !destinationObject.cg_metadata
    ) {
      return false;
    }

    const isOCP = destinationObject.cg_metadata.created_by === 'OCP';
    const isMatchingProduct = destinationObject.cg_metadata.source_product === source.product.toString();
    const isMatchingSource = destinationObject.cg_metadata.source_object_name === sourceObject.object_name;

    return isOCP && isMatchingProduct && isMatchingSource;
  }

  public static async inheritSourceSchema(
    source: Source,
    destinationObjectName: string,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null,
    baseType?: string
  ): Promise<FieldMappingEntry[]> {
    if (!sourceStore || !destinationStore) {
      return [];
    }
    const dataSyncCgObjectStore = destinationStore as DataSyncCgObjectsStore;
    const sourceObject = sourceStore.getSourceObjectDefinition(source.object.object_name);

    if (!sourceObject) {
      return [];
    }

    // Fetch fields lazily if not already loaded (supports lazy loading stores like CMP)
    if (sourceObject.fields.length === 0) {
      const fields = await sourceStore.getSourceObjectFields(source.object.object_name);
      sourceObject.fields = fields;
    }

    if (sourceObject.fields.length === 0) {
      return [];
    }

    const destinationObject = DataSyncObjectDefinitionService.convertToCGObject(
      source.product,
      sourceStore,
      source.object.object_name,
      destinationObjectName,
      dataSyncCgObjectStore?.getBaseTypeDefinitions() || [],
      baseType
    );

    // Check if destination object already exists in store
    const existingDestinationObject = destinationStore?.getDestinationObjectDefinition(destinationObjectName);

    if (existingDestinationObject) {
      const existingRequiredFields = existingDestinationObject.fields.filter((field) => field.required);

      // Add existing required fields to the transient object if they don't already exist
      existingRequiredFields.forEach((requiredField) => {
        const existingField = destinationObject.fields.find((field) => field.name === requiredField.name);
        if (!existingField) {
          destinationObject.fields.push({ ...requiredField });
        } else {
          existingField.required = true;
        }
      });
    }

    destinationStore?.setTransientDestinationObject(destinationObject);

    // Initialize empty field mappings
    const fieldMappingEntries: FieldMappingEntry[] = [];

    // create mapping for mandatory fields
    for (const field of destinationObject.fields.filter((desField) => desField.required)) {
      fieldMappingEntries.push({
        sourceField: INCOMPLETE,
        destinationField: field.name,
        disabled: false
      });
    }

    // Filter source fields to exclude _modified and _deleted if source is CGR
    const filteredSourceFields = DataSyncObjectDefinitionService
      .filterSourceFields(sourceObject.fields, source.product);

    for (const sourceField of filteredSourceFields) {
      this.createFieldMappings(source.product, baseType, fieldMappingEntries, sourceField, sourceField.name);
    }
    return fieldMappingEntries;
  }

  public static async generateMappingsForInheritedSchema(
    source: Source,
    destination: Destination,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null,
    existingFieldMappings: FieldMappingEntry[]
  ): Promise<{
    mappings: FieldMappingEntry[];
    updatedFields: FieldDefinition[];
  }> {

    const destinationObjectName = destination.object.object_name;
    const destinationObjectFields = await destinationStore?.getDestinationObjectFields(
      destinationObjectName
    );

    if (!destinationObjectFields) {
      return { mappings: [], updatedFields: [] };
    }

    const transientObjectDefinition = {
      object_name: destinationObjectName,
      source_product: source.product,
      source_object_name: source.object.object_name,
      created_by: 'OCP',
      fields: JSON.parse(JSON.stringify(destinationObjectFields))
    } as CGObjectDefinition;

    const sourceObjectFields = sourceStore?.getSourceObjectDefinition(
      source.object.object_name
    )?.fields || [];

    let newFieldMappings = [...existingFieldMappings];

    // Filter source fields to exclude _modified and _deleted if source is CGR
    const filteredSourceFields = DataSyncObjectDefinitionService
      .filterSourceFields(sourceObjectFields, source.product);

    for (const field of filteredSourceFields) {
      // Handle nested fields recursively to create mappings for all source fields
      newFieldMappings = this.checkAndCreateMissingFieldMappings(
        source.product,
        field,
        field.name,
        newFieldMappings,
        transientObjectDefinition,
        filteredSourceFields
      );
    }

    destinationStore?.setTransientDestinationObject(transientObjectDefinition);
    return {
      mappings: newFieldMappings,
      updatedFields: transientObjectDefinition.fields,
    };
  }

  public static filterDisableFields(
    objectDefinition: ObjectDefinition,
    fieldMappings: FieldMappingEntry[]
  ): FieldDefinition[] {
    const disabledFieldPaths = new Set(fieldMappings.filter((m) => m.disabled).map((m) => m.destinationField));

    // Deep clone the fields to avoid mutating the original
    const filteredFields = JSON.parse(JSON.stringify(objectDefinition?.fields || [])) as FieldDefinition[];

    // Recursively remove disabled nested fields
    const filterNestedFields = (fields: FieldDefinition[], pathPrefix: string = ''): FieldDefinition[] => {
      return fields.filter((field) => {
        const currentPath = pathPrefix ? `${pathPrefix}${FIELD_PATH_SEPARATOR}${field.name}` : field.name;

        // If this field itself is disabled, remove it
        if (disabledFieldPaths.has(currentPath)) {
          return false;
        }

        // If this field has nested fields, filter them recursively
        if (field.object_schema?.fields) {
          field.object_schema.fields = filterNestedFields(field.object_schema?.fields, currentPath);
        }

        // If this field has array fields, filter them recursively
        if (field.array && isObjectArray(field.array)) {
          field.array.fields = filterNestedFields(field.array.fields, currentPath);
        }

        return true;
      });
    };

    return filterNestedFields(filteredFields);
  }

  public static async determineInitialMappingMode(
    dataSync: DataSyncItem,
    sourceStore: DataSyncObjectsStore | null,
    destinationStore: DataSyncObjectsStore | null): Promise<MappingMode> {
    const mappingMode = await DataSyncMappingService.determineMappingMode(
      dataSync.source, dataSync.destination, sourceStore, destinationStore
    );
    return mappingMode;
  }

  private static createFieldMappings(
    sourceProduct: OptimizelyProduct,
    baseType: string | undefined,
    fieldMappingEntries: FieldMappingEntry[],
    field: FieldDefinition,
    fieldPath: string
  ): void {
    if (field.object_schema?.fields) {
      for (const nestedField of field.object_schema?.fields) {
        const nestedFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${nestedField.name}`;
        this.createFieldMappings(sourceProduct, baseType, fieldMappingEntries, nestedField, nestedFieldPath);
      }
    } else if (field.array && isObjectArray(field.array)) {
      for (const nestedField of field.array.fields) {
        const nestedFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${nestedField.name}`;
        this.createFieldMappings(sourceProduct, baseType, fieldMappingEntries, nestedField, nestedFieldPath);
      }
    } else {
      const hasBaseType = !!baseType;
      const isCGBaseContractField = DataSyncObjectDefinitionService.isCGBaseContractField(sourceProduct, field.name);

      if (isCGBaseContractField && hasBaseType) {
        return;
      }

      let destinationFieldPath = fieldPath;
      if (isCGBaseContractField && !hasBaseType) {
        destinationFieldPath = field.name.split('.')[1];
      }

      if (!fieldMappingEntries.some((pair) => pair.destinationField === fieldPath)) {
        fieldMappingEntries.push({
          sourceField: fieldPath,
          destinationField: destinationFieldPath,
          disabled: false
        });
      }
    }
  }

  /**
   * Add a field to the correct nested level in the transient object definition
   * Traverses based on sourceFieldPath and creates necessary parent structure from sourceFields
   */
  private static addFieldToTransientObject(
    sourceFieldPath: string,
    sourceFields: FieldDefinition[],
    transientFields: FieldDefinition[]
  ): void {
    const pathParts = sourceFieldPath.split(FIELD_PATH_SEPARATOR);

    if (pathParts.length === 1) {
      // Top-level field - find in sourceFields and add if it doesn't exist in transientFields
      const sourceField = sourceFields.find((f) => f.name === sourceFieldPath);
      const fieldExists = transientFields.find((f) => f.name === sourceFieldPath);

      if (sourceField && !fieldExists) {
        transientFields.push({ ...sourceField });
      }
      return;
    }

    // For nested fields, traverse the path step by step
    const [currentFieldName, ...remainingPath] = pathParts;

    const currentSourceField = sourceFields.find((f) => f.name === currentFieldName);
    if (!currentSourceField) {
      return;
    }

    let currentTransientField = transientFields.find((f) => f.name === currentFieldName);
    if (!currentTransientField) {
      currentTransientField = { ...currentSourceField };
      transientFields.push(currentTransientField);
    }

    let nextSourceFields: FieldDefinition[];
    let nextTransientFields: FieldDefinition[];

    if (currentSourceField.object_schema?.fields) {
      if (!currentTransientField.object_schema) {
        currentTransientField.object_schema = { fields: [] };
      }
      nextSourceFields = currentSourceField.object_schema.fields;
      nextTransientFields = currentTransientField.object_schema.fields;
    } else if (currentSourceField.array && isObjectArray(currentSourceField.array)) {
      if (!currentTransientField.array || !isObjectArray(currentTransientField.array)) {
        currentTransientField.array = { type: 'object', fields: [] };
      }
      nextSourceFields = currentSourceField.array.fields;
      nextTransientFields = currentTransientField.array.fields;
    } else if (currentSourceField.array) {
      if (!currentTransientField.array) {
        currentTransientField.array = { ...currentSourceField.array };
      }
      return;
    } else {
      return;
    }

    // Recursively add to the nested structure
    this.addFieldToTransientObject(
      remainingPath.join(FIELD_PATH_SEPARATOR),
      nextSourceFields,
      nextTransientFields
    );
  }

  /**
   * Recursively check if source fields exist in mappings and create disabled mappings for missing ones
   */
  private static checkAndCreateMissingFieldMappings(
    sourceProduct: OptimizelyProduct,
    sourceField: FieldDefinition,
    fieldPath: string,
    fieldMappings: FieldMappingEntry[],
    transientObjectDefinition: CGObjectDefinition,
    allSourceFields: FieldDefinition[]
  ): FieldMappingEntry[] {
    if (sourceField.object_schema?.fields) {
      for (const nestedField of sourceField.object_schema.fields) {
        const nestedFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${nestedField.name}`;
        fieldMappings = this.checkAndCreateMissingFieldMappings(
          sourceProduct,
          nestedField,
          nestedFieldPath,
          fieldMappings,
          transientObjectDefinition,
          allSourceFields
        );
      }
    } else if (sourceField.array && isObjectArray(sourceField.array)) {
      for (const arrayField of sourceField.array.fields) {
        const arrayFieldPath = `${fieldPath}${FIELD_PATH_SEPARATOR}${arrayField.name}`;
        fieldMappings = this.checkAndCreateMissingFieldMappings(
          sourceProduct,
          arrayField,
          arrayFieldPath,
          fieldMappings,
          transientObjectDefinition,
          allSourceFields
        );
      }
    } else {
      if (DataSyncObjectDefinitionService.isCGBaseContractField(sourceProduct, sourceField.name)) {
        return fieldMappings;
      }

      const existingMapping = fieldMappings.some(
        (m) => m.sourceField === fieldPath && m.destinationField === fieldPath
      );
      if (!existingMapping) {
        fieldMappings.push({
          sourceField: fieldPath,
          destinationField: fieldPath,
          disabled: true,
        });
        this.addFieldToTransientObject(
          fieldPath,
          allSourceFields,
          transientObjectDefinition.fields
        );
      }
    }

    return fieldMappings;
  }
}
